import { useCallback } from 'react';

import { RecurrenceInboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useGetPaginatedRecurrenceInboxEntries as useGetPaginatedRecurrenceInboxEntriesService } from '../../services/recurrenceInboxEntries.service';
import { useInboxEntriesStore } from '../../store';

interface FetchRecurrenceInboxEntriesParams {
  limit: number;
  exclusiveStartKey?: string;
}

interface FetchRecurrenceInboxEntriesResult {
  recurrenceInboxEntries: RecurrenceInboxEntry[];
  exclusiveStartKey: string | null;
}

interface UseFetchRecurrenceInboxEntriesReturn {
  fetchRecurrenceInboxEntries: (
    params: FetchRecurrenceInboxEntriesParams,
  ) => Promise<FetchRecurrenceInboxEntriesResult | null>;
}

const EMPTY_RECURRENCE_RESULT: FetchRecurrenceInboxEntriesResult = {
  recurrenceInboxEntries: [],
  exclusiveStartKey: null,
};

export function useFetchRecurrenceInboxEntries(): UseFetchRecurrenceInboxEntriesReturn {
  const { getPaginatedRecurrenceInboxEntries } =
    useGetPaginatedRecurrenceInboxEntriesService();

  const activeTaskGroupId = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup.taskGroupId,
  );

  const fetchRecurrenceInboxEntries = useCallback(
    async (
      params: FetchRecurrenceInboxEntriesParams,
    ): Promise<FetchRecurrenceInboxEntriesResult | null> => {
      if (!activeTaskGroupId) {
        return EMPTY_RECURRENCE_RESULT;
      }
      try {
        return await getPaginatedRecurrenceInboxEntries({
          ...params,
          taskGroupId: activeTaskGroupId,
        });
      } catch (error) {
        log.error('Error fetching recurrence inbox entries:', error);
        return null;
      }
    },
    [getPaginatedRecurrenceInboxEntries, activeTaskGroupId],
  );

  return { fetchRecurrenceInboxEntries };
}
